import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';

import { appointmentActionSession } from './appointmentActionSession';
import { participantCalendarAppointmentRecord } from './calendarAppointmentProjection';

import type { ParticipantCalendarAppointmentActionRecord } from './calendarAppointmentProjection';
import type { DecodedBrowserAppointment } from './types';
import type { Session } from '@/types';

import { useGetAppointmentsQuery } from '@/store/api/appointmentApi';

// Recompute time-sensitive filtering once a minute so a long-open tab does
// not keep an expired appointment (and its Join / Cancel / Reschedule
// buttons) visible past its `endAt`. Exported so callers and tests can
// reason about the interval without importing the setInterval id.
export const APPOINTMENT_CLOCK_INTERVAL_MS = 60_000;

export const useAppointmentClock = (enabled = true) => {
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(
      () => setNowTick(Date.now()),
      APPOINTMENT_CLOCK_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [enabled]);
  return nowTick;
};

// Lifecycle states that should still render in an "upcoming" surface.
// Pending states stay visible so an in-flight cancel/reschedule command
// does not remove its own card mid-poll.
const RENDERABLE_LIFECYCLE_STATUSES: ReadonlySet<
  DecodedBrowserAppointment['lifecycleStatus']
> = new Set(['scheduled', 'reschedule_pending', 'cancellation_pending']);

// A native appointment is only actionable when both participant person ids
// are present and non-empty. `appointmentActionSession` writes these into
// `Session.coachId` / `Session.userId`, and the MentoCal cancel/reschedule
// dialogs read them directly on submit — an empty id would either hit an
// undefined-behavior path in MentoCal or send a command the server cannot
// route.
//
// The wire contract requires `expected_identities.{coach,member}.person_id`
// as a UUID, so this guard is defense-in-depth against contract drift, but
// it also rejects the (in-spec) case where a contract update reintroduces
// nullable identity fields. It runs at the hook boundary so every caller
// gets the same guarantee: any appointment surfaced downstream has both
// identities.
const hasActionableIdentities = (
  appointment: DecodedBrowserAppointment,
): boolean =>
  Boolean(
    appointment.expectedIdentities.coach.personId &&
    appointment.expectedIdentities.member.personId,
  );

export interface UseNativeAppointmentRowsOptions {
  // Cache-key discriminant only; not sent on the wire. The RTK codec ignores
  // it when composing the request URL.
  userId: string;
  actingRole?: 'member' | 'coach';
  // When false, the hook skips the query entirely (skipToken). Defaults to
  // true.
  enabled?: boolean;
}

export interface UseNativeAppointmentRowsResult {
  // Filtered + sorted native appointments the current viewer can render.
  //   filter: booking_status === 'confirmed'
  //           AND lifecycle_status in {scheduled, reschedule_pending,
  //                                    cancellation_pending}
  //           AND currentSchedule.endAt >= nowTick
  //   sort:   ascending by Date.parse(currentSchedule.startAt); ties by id.
  nativeAppointments: DecodedBrowserAppointment[];
  nativeActionRecords: ParticipantCalendarAppointmentActionRecord[];
  // Same items as `nativeAppointments`, in the same order, adapted to the
  // Session shape the legacy Sessions timeline and MentoCal dialogs consume.
  // The adapter sets `location` only when `canRenderNativeJoin` holds.
  nativeSessions: Session[];
  // Every appointment the server returned, keyed by id — including
  // cancelled, cancellation_pending, and already-expired rows. Callers use
  // this to reconcile a paired legacy Session projection (e.g. the
  // /sessions/upcoming scrub of stale `location` when the corresponding
  // native says join is no longer valid).
  nativeAppointmentsById: Map<string, DecodedBrowserAppointment>;
  // The current "now" the filter memo was keyed against. Callers thread
  // this into predicates such as `canRenderNativeJoin(native, nowTick)` so
  // the two surfaces stay in sync with a single ticking clock.
  nowTick: number;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => void;
}

// Hook that owns everything a caller needs to render native Moments
// Appointments on a member surface:
//
//   - the RTK query (with skipToken gating when disabled),
//   - the once-a-minute clock ticker,
//   - the booking_status + lifecycle_status + endAt filter,
//   - the (startAt, id) sort,
//   - the `appointmentActionSession` adapter, and
//   - the id → appointment map used by the /sessions/upcoming scrub.
//
// Callers pick which shape they need. `MemberAppointments` groups
// `nativeAppointments` by month; `Sessions/Upcoming` reconciles
// `nativeSessions` against the legacy pagination page and scrubs stale
// `location` fields using `nativeAppointmentsById`.
//
// The hook is deliberately unopinionated about grouping and reconciliation
// so it stays a deep module over one thing: the rules for producing
// renderable native rows from a fresh Appointment page.
export const useNativeAppointmentRows = ({
  actingRole,
  userId,
  enabled = true,
}: UseNativeAppointmentRowsOptions): UseNativeAppointmentRowsResult => {
  const {
    currentData: appointmentPage,
    isError,
    isLoading,
    isSuccess,
    refetch,
  } = useGetAppointmentsQuery(
    enabled ? { ...(actingRole ? { actingRole } : {}), userId } : skipToken,
  );

  // Ticking `now` state. Without this the filter memo below only re-derives
  // when `appointmentPage` changes, so a tab opened at 1:59 would keep a
  // 2:00 appointment (and its Join / Cancel / Reschedule buttons) visible
  // indefinitely past 2:00 because nothing else in this hook causes the
  // memo to re-run.
  //
  // Gate the interval on `enabled` so a Coach/Support/Overview viewer —
  // who skips the fetch entirely — does not incur a re-render every 60
  // seconds for state no one downstream reads.
  const nowTick = useAppointmentClock(enabled);

  const nativeAppointments = useMemo<DecodedBrowserAppointment[]>(() => {
    if (!appointmentPage) return [];
    const eligible = appointmentPage.appointments.filter(
      (appointment) =>
        appointment.bookingStatus === 'confirmed' &&
        RENDERABLE_LIFECYCLE_STATUSES.has(appointment.lifecycleStatus) &&
        Date.parse(appointment.currentSchedule.endAt) >= nowTick &&
        hasActionableIdentities(appointment),
    );
    return [...eligible].sort((left, right) => {
      const delta =
        Date.parse(left.currentSchedule.startAt) -
        Date.parse(right.currentSchedule.startAt);
      if (delta !== 0) return delta;
      return left.id.localeCompare(right.id);
    });
  }, [appointmentPage, nowTick]);

  const nativeActionRecords = useMemo(
    () => nativeAppointments.map(participantCalendarAppointmentRecord),
    [nativeAppointments],
  );
  const nativeSessions = useMemo<Session[]>(
    () => nativeAppointments.map(appointmentActionSession),
    [nativeAppointments],
  );

  // Includes cancelled + expired appointments; the /sessions/upcoming scrub
  // needs those rows to detect a paired legacy Session projection whose
  // stored HTTPS `location` is stale.
  //
  // Excludes appointments that fail `hasActionableIdentities`: the scrub
  // and reconciliation both key off `appointment.id`, and a broken native
  // at a newer version would suppress its paired legacy row via
  // /sessions/upcoming's version comparison while itself being filtered
  // from the render-eligible list — silently removing the row entirely.
  // Keeping those rows out of the map makes them invisible to the merge
  // pipeline, so the paired legacy Session proceeds as if no native exists
  // for that id (which is the safe fallback).
  //
  // When the same id appears more than once in a page, retain the
  // highest-version record. Mirrors the same version-dedup rule
  // `SessionTimeline` uses for its `latestAppointmentById` map so the
  // /sessions/upcoming reconciliation and the timeline never disagree
  // about which snapshot of an appointment they see. The wire contract
  // does not currently permit repeated ids on the same page, but the
  // guard is defense-in-depth against a future pagination change.
  const nativeAppointmentsById = useMemo<
    Map<string, DecodedBrowserAppointment>
  >(() => {
    const map = new Map<string, DecodedBrowserAppointment>();
    if (!appointmentPage) return map;
    appointmentPage.appointments.forEach((appointment) => {
      if (!hasActionableIdentities(appointment)) return;
      const existing = map.get(appointment.id);
      if (!existing || appointment.version > existing.version) {
        map.set(appointment.id, appointment);
      }
    });
    return map;
  }, [appointmentPage]);

  return {
    isError,
    isLoading,
    isSuccess,
    nativeActionRecords,
    nativeAppointments,
    nativeAppointmentsById,
    nativeSessions,
    nowTick,
    refetch,
  };
};
